import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, X, CheckCircle2, PenLine } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return iso; } };

export const AutopilotPanel = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [topics, setTopics] = useState("");
  const [saving, setSaving] = useState(false);
  const [count, setCount] = useState(4);

  const load = () => api.get("/ops/autopilot").then((r) => {
    setData(r.data);
    if (!form) { setForm(r.data.settings); setTopics((r.data.settings.custom_topics || []).join("\n")); }
  }).catch(() => setData({ ai_ready: false, settings: {}, scheduled: [] }));

  useEffect(() => { load(); }, []); // eslint-disable-line
  const writing = data?.last_run && !data.last_run.finished_at;
  useEffect(() => {
    if (!writing) return;
    const t = setInterval(() => { load(); onChange?.(); }, 8000);
    return () => clearInterval(t);
  }, [writing]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, custom_topics: topics.split("\n").map((t) => t.trim()).filter(Boolean) };
      await api.put("/ops/autopilot", body);
      toast.success("Autopilot settings saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const generate = async (schedule) => {
    try {
      await api.post("/ops/autopilot/generate", null, { params: { count, schedule } });
      toast.success(`Writing ${count} article${count > 1 ? "s" : ""} — they'll appear as they finish`);
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
  };

  if (!data) return null;
  const on = !!data.settings?.enabled;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="autopilot-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 grid place-items-center"><Sparkles className="w-5 h-5" /></div>
          <div>
            <div className="font-heading font-bold text-lg flex items-center gap-2">Content Autopilot
              <span data-testid="autopilot-state" className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${on ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}>{on ? `ON · ${data.settings.mode === "auto" ? "auto-publish" : "drafts for approval"}` : "OFF"}</span>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="autopilot-queue-summary">
              {data.scheduled.length} article{data.scheduled.length === 1 ? "" : "s"} scheduled ahead
              {data.scheduled.length > 0 && <> (next {fmt(data.scheduled[0].scheduled_at)}, last {fmt(data.scheduled[data.scheduled.length - 1].scheduled_at)})</>}
              {data.pending_drafts > 0 && <> · {data.pending_drafts} autopilot draft{data.pending_drafts > 1 ? "s" : ""} awaiting approval</>}
              {" · "}{data.topics_remaining} topics in the bank
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {writing ? (
            <span className="inline-flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300" data-testid="autopilot-writing">
              <Loader2 className="w-4 h-4 animate-spin" /> Writing… {data.last_run.done}/{data.last_run.total}
            </span>
          ) : (
            <>
              <select value={count} onChange={(e) => setCount(Number(e.target.value))} data-testid="autopilot-count"
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 text-sm">
                {[1, 2, 4, 6, 8].map((n) => <option key={n} value={n}>{n} article{n > 1 ? "s" : ""}</option>)}
              </select>
              <button onClick={() => generate(true)} disabled={!data.ai_ready} data-testid="autopilot-generate-schedule"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg px-3 py-2 text-sm font-semibold disabled:opacity-50">
                <PenLine className="w-4 h-4" /> Write &amp; schedule weekly
              </button>
              <button onClick={() => generate(false)} disabled={!data.ai_ready} data-testid="autopilot-generate-draft"
                className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm font-semibold hover:border-amber-400 disabled:opacity-50">Write as drafts</button>
            </>
          )}
          <button onClick={() => setOpen((o) => !o)} data-testid="autopilot-cfg-open" className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm font-semibold hover:border-indigo-400">Settings</button>
        </div>
      </div>
      {!data.ai_ready && <p className="text-xs text-amber-600 mt-3">AI key isn't configured in this environment — autopilot can't write here.</p>}
      {data.last_run?.finished_at && !writing && (
        <p className="text-xs text-slate-500 mt-3" data-testid="autopilot-last-run">Last run: {data.last_run.done} written{data.last_run.errors ? `, ${data.last_run.errors} failed` : ""}{data.last_run.titles?.length ? ` — ${data.last_run.titles.slice(-3).join(" · ")}` : ""}</p>
      )}

      {open && form && (
        <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800 space-y-4" data-testid="autopilot-cfg-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Keep the queue full automatically</h3>
            <button onClick={() => setOpen(false)} data-testid="autopilot-cfg-close" className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer" data-testid="autopilot-enabled-toggle">
            <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 accent-amber-500" />
            Turn Autopilot on — whenever fewer than <b className="mx-1">{form.min_queue}</b> articles are scheduled ahead, write more.
          </label>
          <div className="grid sm:grid-cols-4 gap-3 text-sm">
            <label className="block"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Mode</span>
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="autopilot-mode" className="w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5">
                <option value="auto">Fully automatic (schedule &amp; publish)</option>
                <option value="draft">Draft for my approval</option>
              </select></label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Keep ahead</span>
              <select value={form.min_queue} onChange={(e) => setForm({ ...form, min_queue: Number(e.target.value) })} data-testid="autopilot-min-queue" className="w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5">
                {[1, 2, 3, 4, 6, 8].map((n) => <option key={n} value={n}>{n} week{n > 1 ? "s" : ""}</option>)}
              </select></label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Publish day</span>
              <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })} data-testid="autopilot-weekday" className="w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5">
                {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select></label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Hour (UTC)</span>
              <input type="number" min={0} max={23} value={form.hour_utc} onChange={(e) => setForm({ ...form, hour_utc: Number(e.target.value) })} data-testid="autopilot-hour" className="w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5" /></label>
          </div>
          <label className="block text-sm"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Your own topics (one per line — used first)</span>
            <textarea value={topics} onChange={(e) => setTopics(e.target.value)} rows={3} data-testid="autopilot-topics" placeholder="e.g. Why the service drive greeting matters more than the coupon"
              className="w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-3 py-2" /></label>
          <button onClick={save} disabled={saving} data-testid="autopilot-cfg-save" className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save settings
          </button>
        </div>
      )}
    </div>
  );
};
